import React, { useState, useCallback } from 'react';
import { 
  Target, 
  Compass, 
  Ruler, 
  Calculator, 
  Shapes, 
  Repeat,
  CheckCircle,
  TrendingUp,
  Circle,
  Square,
  Triangle,
  RotateCcw
} from 'lucide-react';

interface Tool {
  id: string;
  name: string;
  description: string;
  type: string;
  icon: string;
}

interface MathToolboxResource {
  type: 'math_toolbox';
  name: string;
  title: string;
  description: string;
  tools: Tool[];
  show_tool_usage: boolean;
}

interface MathToolboxComponentProps {
  resource: MathToolboxResource;
  onAction?: (action: string, data: any) => void;
}

export const MathToolboxComponent: React.FC<MathToolboxComponentProps> = ({
  resource,
  onAction
}) => {
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [toolUsage, setToolUsage] = useState<Record<string, number>>({});
  const [recentTools, setRecentTools] = useState<string[]>([]);

  const getToolIcon = (iconName: string) => {
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      Target,
      Compass,
      Ruler,
      Calculator,
      Shapes,
      Repeat
    };
    return iconMap[iconName] || Target;
  };

  const getToolColor = (type: string) => {
    const colorMap: { [key: string]: string } = {
      sorting: 'bg-blue-100 text-blue-800 border-blue-200',
      spatial: 'bg-green-100 text-green-800 border-green-200',
      geometry: 'bg-purple-100 text-purple-800 border-purple-200',
      measurement: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      logic: 'bg-pink-100 text-pink-800 border-pink-200',
      calculation: 'bg-red-100 text-red-800 border-red-200'
    };
    return colorMap[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getToolInstructions = (toolId: string) => {
    const instructionsMap: { [key: string]: string[] } = {
      'shape_creator': [
        'Cliquez et glissez pour dessiner des formes',
        'Utilisez les boutons pour choisir le type de forme',
        'Modifiez la taille et la couleur selon vos besoins'
      ],
      'measurement_ruler': [
        'Cliquez sur deux points pour mesurer la distance',
        'Utilisez les unités de mesure disponibles',
        'Comparez les longueurs des objets'
      ],
      'sorting_organizer': [
        'Glissez les objets dans les bonnes catégories',
        'Utilisez les critères de tri (couleur, forme, taille)',
        'Vérifiez que le classement est correct'
      ],
      'pattern_creator': [
        'Cliquez pour ajouter des éléments au pattern',
        'Répétez la séquence selon la règle',
        'Testez votre pattern en le complétant'
      ],
      'calculator': [
        'Cliquez sur les chiffres et opérations',
        'Utilisez les boutons pour effectuer des calculs',
        'Vérifiez vos résultats'
      ],
      'spatial_planner': [
        'Cliquez et glissez pour placer les objets',
        'Organisez l\'espace selon les consignes',
        'Vérifiez les positions relatives'
      ]
    };
    return instructionsMap[toolId] || [
      'Sélectionnez l\'outil pour commencer',
      'Suivez les instructions à l\'écran',
      'Utilisez les contrôles disponibles'
    ];
  };

  // États pour le créateur de formes (persistants)
  const [shapes, setShapes] = useState<Array<{id: string, type: string, x: number, y: number, size: number, color: string}>>([]);
  const [selectedShape, setSelectedShape] = useState('circle');
  const [selectedColor, setSelectedColor] = useState('#3B82F6');
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Fonctions du créateur de formes avec useCallback
  const addShape = useCallback(() => {
    const newShape = {
      id: Date.now().toString(),
      type: selectedShape,
      x: Math.random() * 200 + 50,
      y: Math.random() * 100 + 50,
      size: 30,
      color: selectedColor
    };
    setShapes(prev => [...prev, newShape]);
  }, [selectedShape, selectedColor]);

  const clearShapes = useCallback(() => {
    setShapes([]);
    setSelectedShapeId(null);
  }, []);

  const deleteShape = useCallback((shapeId: string) => {
    setShapes(prev => prev.filter(shape => shape.id !== shapeId));
    if (selectedShapeId === shapeId) {
      setSelectedShapeId(null);
    }
  }, [selectedShapeId]);

  const selectShape = useCallback((shapeId: string) => {
    setSelectedShapeId(shapeId);
  }, []);

  const updateShapePosition = useCallback((shapeId: string, x: number, y: number) => {
    setShapes(prev => prev.map(shape => 
      shape.id === shapeId ? { ...shape, x, y } : shape
    ));
  }, []);

  const updateShapeSize = useCallback((shapeId: string, size: number) => {
    setShapes(prev => prev.map(shape => 
      shape.id === shapeId ? { ...shape, size: Math.max(20, Math.min(80, size)) } : shape
    ));
  }, []);

  const updateShapeColor = useCallback((shapeId: string, color: string) => {
    setShapes(prev => prev.map(shape => 
      shape.id === shapeId ? { ...shape, color } : shape
    ));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent, shapeId: string) => {
    e.preventDefault();
    const shape = shapes.find(s => s.id === shapeId);
    if (shape) {
      setSelectedShapeId(shapeId);
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - shape.x,
        y: e.clientY - shape.y
      });
    }
  }, [shapes]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && selectedShapeId) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      updateShapePosition(selectedShapeId, newX, newY);
    }
  }, [isDragging, selectedShapeId, dragOffset, updateShapePosition]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  // Composant pour l'interface du créateur de formes
  const ShapeCreatorInterface = useCallback(() => {
    const selectedShapeData = shapes.find(s => s.id === selectedShapeId);

    return (
      <div className="bg-white rounded-lg p-3 border">
        <div className="flex items-center justify-between mb-3">
          <h6 className="text-sm font-semibold text-gray-800">Créateur de Formes</h6>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">{shapes.length} forme{shapes.length > 1 ? 's' : ''}</span>
            <button
              onClick={clearShapes}
              className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
            >
              <RotateCcw className="w-3 h-3" />
            </button>
          </div>
        </div>
        
        {/* Outils de création */}
        <div className="mb-3">
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => setSelectedShape('circle')}
              className={`p-2 rounded border ${selectedShape === 'circle' ? 'bg-blue-100 border-blue-300' : 'bg-gray-100 border-gray-300'}`}
              title="Cercle"
            >
              <Circle className="w-4 h-4" />
            </button>
            <button
              onClick={() => setSelectedShape('square')}
              className={`p-2 rounded border ${selectedShape === 'square' ? 'bg-blue-100 border-blue-300' : 'bg-gray-100 border-gray-300'}`}
              title="Carré"
            >
              <Square className="w-4 h-4" />
            </button>
            <button
              onClick={() => setSelectedShape('triangle')}
              className={`p-2 rounded border ${selectedShape === 'triangle' ? 'bg-blue-100 border-blue-300' : 'bg-gray-100 border-gray-300'}`}
              title="Triangle"
            >
              <Triangle className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-gray-600">Couleur:</span>
            <input
              type="color"
              value={selectedColor}
              onChange={(e) => setSelectedColor(e.target.value)}
              className="w-8 h-6 rounded border"
            />
          </div>
          
          <button
            onClick={addShape}
            className="w-full px-3 py-2 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors"
          >
            + Ajouter une forme
          </button>
        </div>

        {/* Zone de dessin */}
        <div 
          className="relative h-40 bg-gray-50 rounded border-2 border-dashed border-gray-300 overflow-hidden"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {shapes.map(shape => (
            <div
              key={shape.id}
              className={`absolute cursor-move transition-all ${
                selectedShapeId === shape.id 
                  ? 'ring-2 ring-blue-400 ring-opacity-50' 
                  : 'hover:ring-1 hover:ring-gray-400'
              }`}
              style={{
                left: shape.x,
                top: shape.y,
                width: shape.size,
                height: shape.size,
                backgroundColor: shape.color,
                borderRadius: shape.type === 'circle' ? '50%' : '0',
                clipPath: shape.type === 'triangle' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : 'none',
                transform: isDragging && selectedShapeId === shape.id ? 'scale(1.05)' : 'scale(1)',
                zIndex: selectedShapeId === shape.id ? 10 : 1
              }}
              onMouseDown={(e) => handleMouseDown(e, shape.id)}
              onClick={() => selectShape(shape.id)}
            >
              {/* Bouton de suppression */}
              {selectedShapeId === shape.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteShape(shape.id);
                  }}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 flex items-center justify-center"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {shapes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs">
              Cliquez sur "Ajouter une forme" pour commencer
            </div>
          )}
        </div>

        {/* Panneau de contrôle pour la forme sélectionnée */}
        {selectedShapeData && (
          <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
            <div className="text-xs font-medium text-blue-800 mb-2">
              Forme sélectionnée ({selectedShapeData.type})
            </div>
            
            <div className="space-y-2">
              {/* Contrôle de taille */}
              <div>
                <label className="text-xs text-gray-600">Taille: {selectedShapeData.size}px</label>
                <input
                  type="range"
                  min="20"
                  max="80"
                  value={selectedShapeData.size}
                  onChange={(e) => updateShapeSize(selectedShapeData.id, parseInt(e.target.value))}
                  className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              
              {/* Contrôle de couleur */}
              <div>
                <label className="text-xs text-gray-600">Couleur:</label>
                <input
                  type="color"
                  value={selectedShapeData.color}
                  onChange={(e) => updateShapeColor(selectedShapeData.id, e.target.value)}
                  className="w-full h-6 rounded border"
                />
              </div>
              
              {/* Bouton de suppression */}
              <button
                onClick={() => deleteShape(selectedShapeData.id)}
                className="w-full px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 transition-colors"
              >
                Supprimer cette forme
              </button>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-3 text-xs text-gray-500">
          💡 <strong>Astuce:</strong> Cliquez sur une forme pour la sélectionner, puis glissez-la pour la déplacer
        </div>
      </div>
    );
  }, [shapes, selectedShape, selectedColor, selectedShapeId, isDragging, addShape, clearShapes, selectShape, deleteShape, updateShapeSize, updateShapeColor, handleMouseDown, handleMouseMove, handleMouseUp]);

  // États pour la calculatrice (persistants)
  const [calculatorDisplay, setCalculatorDisplay] = useState('0');
  const [calculatorOperation, setCalculatorOperation] = useState<string | null>(null);
  const [calculatorPreviousValue, setCalculatorPreviousValue] = useState<number | null>(null);

  // Fonctions de la calculatrice avec useCallback
  const handleNumber = useCallback((num: string) => {
    setCalculatorDisplay(prev => prev === '0' ? num : prev + num);
  }, []);

  const handleOperation = useCallback((op: string) => {
    setCalculatorPreviousValue(parseFloat(calculatorDisplay));
    setCalculatorOperation(op);
    setCalculatorDisplay('0');
  }, [calculatorDisplay]);

  const calculate = useCallback(() => {
    if (calculatorOperation && calculatorPreviousValue !== null) {
      const current = parseFloat(calculatorDisplay);
      let result = 0;
      
      switch (calculatorOperation) {
        case '+': result = calculatorPreviousValue + current; break;
        case '-': result = calculatorPreviousValue - current; break;
        case '×': result = calculatorPreviousValue * current; break;
        case '÷': result = calculatorPreviousValue / current; break;
      }
      
      setCalculatorDisplay(result.toString());
      setCalculatorOperation(null);
      setCalculatorPreviousValue(null);
    }
  }, [calculatorOperation, calculatorPreviousValue, calculatorDisplay]);

  const clearCalculator = useCallback(() => {
    setCalculatorDisplay('0');
    setCalculatorOperation(null);
    setCalculatorPreviousValue(null);
  }, []);

  // Composant pour l'interface du calculateur
  const CalculatorInterface = useCallback(() => {
    return (
      <div className="bg-white rounded-lg p-3 border">
        <h6 className="text-sm font-semibold text-gray-800 mb-3">Calculateur</h6>
        
        <div className="bg-gray-900 text-white p-3 rounded mb-3 text-right">
          <div className="text-lg font-mono">{calculatorDisplay}</div>
        </div>
        
        <div className="grid grid-cols-4 gap-2">
          <button onClick={clearCalculator} className="col-span-2 px-2 py-2 bg-red-500 text-white rounded text-xs">C</button>
          <button onClick={() => handleOperation('÷')} className="px-2 py-2 bg-orange-500 text-white rounded text-xs">÷</button>
          <button onClick={() => handleOperation('×')} className="px-2 py-2 bg-orange-500 text-white rounded text-xs">×</button>
          
          <button onClick={() => handleNumber('7')} className="px-2 py-2 bg-gray-200 rounded text-xs">7</button>
          <button onClick={() => handleNumber('8')} className="px-2 py-2 bg-gray-200 rounded text-xs">8</button>
          <button onClick={() => handleNumber('9')} className="px-2 py-2 bg-gray-200 rounded text-xs">9</button>
          <button onClick={() => handleOperation('-')} className="px-2 py-2 bg-orange-500 text-white rounded text-xs">-</button>
          
          <button onClick={() => handleNumber('4')} className="px-2 py-2 bg-gray-200 rounded text-xs">4</button>
          <button onClick={() => handleNumber('5')} className="px-2 py-2 bg-gray-200 rounded text-xs">5</button>
          <button onClick={() => handleNumber('6')} className="px-2 py-2 bg-gray-200 rounded text-xs">6</button>
          <button onClick={() => handleOperation('+')} className="px-2 py-2 bg-orange-500 text-white rounded text-xs">+</button>
          
          <button onClick={() => handleNumber('1')} className="px-2 py-2 bg-gray-200 rounded text-xs">1</button>
          <button onClick={() => handleNumber('2')} className="px-2 py-2 bg-gray-200 rounded text-xs">2</button>
          <button onClick={() => handleNumber('3')} className="px-2 py-2 bg-gray-200 rounded text-xs">3</button>
          <button onClick={calculate} className="row-span-2 px-2 py-2 bg-green-500 text-white rounded text-xs">=</button>
          
          <button onClick={() => handleNumber('0')} className="col-span-2 px-2 py-2 bg-gray-200 rounded text-xs">0</button>
          <button onClick={() => setCalculatorDisplay(prev => prev + '.')} className="px-2 py-2 bg-gray-200 rounded text-xs">.</button>
        </div>
      </div>
    );
  }, [calculatorDisplay, handleNumber, handleOperation, calculate, clearCalculator]);

  // États pour l'interface de tri (persistants)
  const [sortingItems, setSortingItems] = useState<Array<{id: number, name: string, category: string | null, correctCategory: string}>>([
    { id: 1, name: '🔴', category: null, correctCategory: 'Rouge' },
    { id: 2, name: '🔵', category: null, correctCategory: 'Bleu' },
    { id: 3, name: '🟡', category: null, correctCategory: 'Jaune' },
    { id: 4, name: '🟢', category: null, correctCategory: 'Vert' },
    { id: 5, name: '🔴', category: null, correctCategory: 'Rouge' },
    { id: 6, name: '🔵', category: null, correctCategory: 'Bleu' }
  ]);
  const [sortingCategories] = useState(['Rouge', 'Bleu', 'Jaune', 'Vert']);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  // Fonctions de l'interface de tri avec useCallback
  const moveToCategory = useCallback((itemId: number, category: string) => {
    setSortingItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, category } : item
    ));
    setDraggedItem(null);
  }, []);

  const resetSorting = useCallback(() => {
    setSortingItems(prev => prev.map(item => ({ ...item, category: null })));
    setDraggedItem(null);
  }, []);

  const handleDragStart = useCallback((itemId: number) => {
    setDraggedItem(itemId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, category: string) => {
    e.preventDefault();
    const itemId = parseInt(e.dataTransfer.getData('itemId'));
    if (itemId) {
      moveToCategory(itemId, category);
    }
  }, [moveToCategory]);

  // Calculer le score
  const getScore = useCallback(() => {
    const correct = sortingItems.filter(item => item.category === item.correctCategory).length;
    const total = sortingItems.length;
    return { correct, total, percentage: Math.round((correct / total) * 100) };
  }, [sortingItems]);

  // Composant pour l'interface de tri
  const SortingInterface = useCallback(() => {
    const score = getScore();
    const unsortedItems = sortingItems.filter(item => !item.category);

    return (
      <div className="bg-white rounded-lg p-3 border">
        <div className="flex items-center justify-between mb-3">
          <h6 className="text-sm font-semibold text-gray-800">Organisateur de Tri</h6>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">
              Score: {score.correct}/{score.total} ({score.percentage}%)
            </span>
            <button
              onClick={resetSorting}
              className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
            >
              <RotateCcw className="w-3 h-3" />
            </button>
          </div>
        </div>
        
        <div className="mb-3">
          <p className="text-xs text-gray-600 mb-2">Glissez les objets dans les bonnes catégories :</p>
          <div className="flex flex-wrap gap-1 min-h-[50px] p-2 bg-gray-50 rounded border-2 border-dashed border-gray-300">
            {unsortedItems.length > 0 ? (
              unsortedItems.map(item => (
                <div
                  key={item.id}
                  className={`p-2 rounded border text-lg cursor-move transition-all ${
                    draggedItem === item.id 
                      ? 'bg-blue-200 border-blue-400 shadow-lg scale-105' 
                      : 'bg-white border-gray-300 hover:bg-blue-50'
                  }`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('itemId', item.id.toString());
                    handleDragStart(item.id);
                  }}
                  onDragEnd={handleDragEnd}
                >
                  {item.name}
                </div>
              ))
            ) : (
              <div className="text-gray-400 text-xs italic">Tous les objets ont été triés !</div>
            )}
          </div>
        </div>
        
        <div className="space-y-2">
          {sortingCategories.map(category => {
            const categoryItems = sortingItems.filter(item => item.category === category);
            const hasCorrectItems = categoryItems.some(item => item.correctCategory === category);
            const hasIncorrectItems = categoryItems.some(item => item.correctCategory !== category);
            
            return (
              <div 
                key={category} 
                className={`border-2 border-dashed rounded p-2 min-h-[50px] transition-colors ${
                  draggedItem ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                }`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, category)}
              >
                <div className="text-xs font-medium text-gray-600 mb-1 flex items-center justify-between">
                  <span>{category}</span>
                  {categoryItems.length > 0 && (
                    <span className={`text-xs px-1 py-0.5 rounded ${
                      hasCorrectItems && !hasIncorrectItems 
                        ? 'bg-green-100 text-green-700' 
                        : hasIncorrectItems 
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {categoryItems.length} objet{categoryItems.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {categoryItems.map(item => (
                    <div 
                      key={item.id} 
                      className={`p-1 rounded text-lg transition-colors ${
                        item.correctCategory === category 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {item.name}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {score.percentage === 100 && (
          <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded text-center">
            <span className="text-green-700 text-xs font-medium">
              🎉 Excellent ! Tous les objets sont dans les bonnes catégories !
            </span>
          </div>
        )}
      </div>
    );
  }, [sortingItems, sortingCategories, draggedItem, handleDragStart, handleDragEnd, handleDragOver, handleDrop, resetSorting, getScore]);

  const selectTool = useCallback((tool: Tool) => {
    setActiveTool(tool.id);
    setToolUsage(prev => ({
      ...prev,
      [tool.id]: (prev[tool.id] || 0) + 1
    }));
    
    // Ajouter à l'historique des outils récents
    setRecentTools(prev => {
      const filtered = prev.filter(id => id !== tool.id);
      return [tool.id, ...filtered].slice(0, 5);
    });

    if (onAction) {
      onAction('tool_selected', {
        toolId: tool.id,
        toolName: tool.name,
        toolType: tool.type,
        usageCount: (toolUsage[tool.id] || 0) + 1
      });
    }
  }, [toolUsage, onAction]);

  const getMostUsedTool = () => {
    const entries = Object.entries(toolUsage);
    if (entries.length === 0) return null;
    return entries.reduce((max, current) => 
      current[1] > max[1] ? current : max
    );
  };

  const totalUsage = Object.values(toolUsage).reduce((sum, count) => sum + count, 0);
  const mostUsed = getMostUsedTool();

  return (
    <div className="w-full p-3 bg-white rounded-lg shadow-sm">
      {/* Header compact */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{resource.title}</h3>
        <p className="text-sm text-gray-600">{resource.description}</p>
      </div>

      {/* Statistiques d'utilisation - Compact */}
      {resource.show_tool_usage && totalUsage > 0 && (
        <div className="bg-blue-50 rounded-lg p-3 mb-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">Statistiques</h4>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-lg font-bold text-blue-600">{totalUsage}</div>
              <div className="text-xs text-blue-700">Total</div>
            </div>
            <div>
              <div className="text-lg font-bold text-blue-600">
                {Object.keys(toolUsage).length}
              </div>
              <div className="text-xs text-blue-700">Outils</div>
            </div>
            <div>
              <div className="text-lg font-bold text-blue-600">
                {mostUsed ? mostUsed[1] : 0}
              </div>
              <div className="text-xs text-blue-700">Top</div>
            </div>
          </div>
        </div>
      )}

      {/* Outils récents - Compact */}
      {recentTools.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Outils récents</h4>
          <div className="flex flex-wrap gap-1">
            {recentTools.map(toolId => {
              const tool = resource.tools.find(t => t.id === toolId);
              if (!tool) return null;
              const Icon = getToolIcon(tool.icon);
              return (
                <button
                  key={toolId}
                  onClick={() => selectTool(tool)}
                  className={`px-2 py-1 rounded border flex items-center space-x-1 ${getToolColor(tool.type)} hover:shadow-sm transition-all text-xs`}
                >
                  <Icon className="w-3 h-3" />
                  <span className="font-medium">{tool.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Grille des outils - Compact */}
      <div className="space-y-2">
        {resource.tools.map((tool) => {
          const Icon = getToolIcon(tool.icon);
          const isActive = activeTool === tool.id;
          const usageCount = toolUsage[tool.id] || 0;
          
          return (
            <div
              key={tool.id}
              className={`border rounded-lg p-3 cursor-pointer transition-all duration-200 ${
                isActive 
                  ? 'border-blue-500 bg-blue-50 shadow-sm' 
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              }`}
              onClick={() => selectTool(tool)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-gray-600'}`} />
                  <h5 className={`text-sm font-semibold ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
                    {tool.name}
                  </h5>
                </div>
                {isActive && <CheckCircle className="w-4 h-4 text-blue-500" />}
              </div>
              
              <p className={`text-xs mb-2 ${isActive ? 'text-blue-700' : 'text-gray-600'}`}>
                {tool.description}
              </p>
              
              <div className="flex items-center justify-between">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${getToolColor(tool.type)}`}>
                  {tool.type}
                </div>
                
                {resource.show_tool_usage && usageCount > 0 && (
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <TrendingUp className="w-3 h-3" />
                    <span>{usageCount}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Outil actif avec interface - Compact */}
      {activeTool && (
        <div className="mt-4">
          <div className="bg-green-50 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2 text-green-700">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Outil actif</span>
              </div>
              <button
                onClick={() => setActiveTool(null)}
                className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 transition-colors"
              >
                Changer d'outil
              </button>
            </div>
            
            <div className="mb-2">
              <p className="text-sm font-semibold text-green-800">
                {resource.tools.find(t => t.id === activeTool)?.name}
              </p>
              <p className="text-xs text-green-700">
                {resource.tools.find(t => t.id === activeTool)?.description}
              </p>
            </div>
          </div>

          {/* Interface de l'outil */}
          {activeTool === 'shape_creator' && <ShapeCreatorInterface />}
          {activeTool === 'calculator' && <CalculatorInterface />}
          {activeTool === 'sorting_organizer' && <SortingInterface />}
          
          {/* Instructions pour les autres outils */}
          {!['shape_creator', 'calculator', 'sorting_organizer'].includes(activeTool) && (
            <div className="bg-white rounded-lg p-3 border">
              <h6 className="text-sm font-semibold text-gray-800 mb-2">
                {resource.tools.find(t => t.id === activeTool)?.name}
              </h6>
              <div className="bg-gray-50 rounded p-2">
                <h5 className="text-xs font-semibold text-gray-800 mb-2">Comment utiliser :</h5>
                <ul className="text-xs text-gray-700 space-y-1">
                  {getToolInstructions(activeTool).map((instruction, index) => (
                    <li key={index} className="flex items-start space-x-1">
                      <span className="text-green-500 mt-0.5">•</span>
                      <span>{instruction}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => {
                  if (onAction) {
                    onAction('tool_used', { toolId: activeTool, action: 'start_using' });
                  }
                }}
                className="mt-2 w-full px-3 py-2 bg-green-500 text-white rounded text-xs hover:bg-green-600 transition-colors"
              >
                Utiliser cet outil
              </button>
            </div>
          )}
        </div>
      )}

      {/* Instructions - Compact */}
      <div className="mt-4 bg-gray-50 rounded-lg p-3">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Instructions</h4>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• Cliquez sur un outil pour le sélectionner</li>
          <li>• L'outil actif est mis en surbrillance</li>
          <li>• Utilisez les outils dans votre projet</li>
          <li>• Les statistiques sont enregistrées</li>
        </ul>
      </div>
    </div>
  );
};

export default MathToolboxComponent;
